import { useCallback } from 'react';
import { useGetFeedQuery, feedApi } from '../../store/services/feed';
import { useSessionHash } from '../../hooks/useSessionHash';
import { useExperimentFlag } from '../../contexts/ExperimentsContext';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { selectFeedPrompts, selectCourses, selectChallenges, selectFeedCache } from '../../store/slices/prompts';

// Тип промпта для frontend
export type Prompt = {
    id: string;
    title: string;
    description: string;
    categories: string[];
    prompt: string;
    prompt_for_example: string;
    example: string;
    imageName: string;
    imageData: {
        urlOptimized: string;
        colors: {
            MainColor: string;
        };
    };
    video: string;
    likes: number;
    visits: number;
    // Properties expected by PromptCard component
    isEditorial: boolean;
    avatar: string;
    image: string;
    isLiked: boolean;
    tags: string[]; // alias for categories
};

type UsePromptsDataReturn = {
    /** Текущий список промптов */
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    prompts?: any[];
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    courses?: any;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    challenges?: any;
    /** Состояние загрузки */
    loading: boolean;
    /** Есть ли еще данные для загрузки */
    hasMore: boolean;
    /** Функция для загрузки следующей порции данных */
    loadMore: () => Promise<void>;
    /** Функция для сброса данных */
    reset?: () => void;
};

/**
 * Хук для работы с данными ленты с поддержкой бесконечной подгрузки
 * @param pageSize - Количество элементов на одной странице
 * @param categories - Массив категорий для фильтрации
 * @param instanceId - Уникальный идентификатор экземпляра хука для генерации уникального hash
 * @returns Объект с данными и методами для управления лентой
 */
export const useFeedData = (
    pageSize: number = 20,
    categories?: string[],
    instanceId?: string,
): UsePromptsDataReturn => {
    const rankingFormula = useExperimentFlag('rankingFormula');
    const formulaParams = useExperimentFlag('formulaParams');
    const dispatch = useAppDispatch();
    const { hash } = useSessionHash();

    // Создаем финальный hash с учетом instanceId
    const finalHash = instanceId ? `${instanceId}_${hash}` : hash;

    // Генерируем ключ кэша
    const categoriesKey = categories ? [...categories].sort().join(',') : '';
    const cacheKey = `${finalHash}_${categoriesKey}`;

    // Получаем данные из Redux
    const prompts = useAppSelector(state => selectFeedPrompts(state, cacheKey));
    const allCourses = useAppSelector(selectCourses);
    const allChallenges = useAppSelector(selectChallenges);
    const cache = useAppSelector(state => selectFeedCache(state, cacheKey));

    // RTK Query только для выполнения запросов
    const { isFetching } = useGetFeedQuery({
        limit: pageSize,
        hash: finalHash,
        categories,
        formula: rankingFormula,
        formulaParams,
    });

    // Функция для загрузки следующей страницы
    const loadMore = useCallback(async (): Promise<void> => {
        if (!cache?.nextCursor || !cache?.hasMorePrompts || isFetching) {
            return;
        }

        try {
            await dispatch(
                feedApi.endpoints.getFeed.initiate(
                    {
                        hash: finalHash,
                        categories,
                        cursor: cache.nextCursor,
                        limit: pageSize,
                        formula: rankingFormula,
                        formulaParams,
                    },
                    { subscribe: false, forceRefetch: true },
                ),
            ).unwrap();
        } catch (error) {
            console.error('Failed to load more feed items:', error);
        }
    }, [
        cache?.nextCursor,
        cache?.hasMorePrompts,
        isFetching, dispatch,
        finalHash, categories,
        pageSize,
        rankingFormula,
        formulaParams,
    ]);

    // Возвращаем курсы и челленджи только если есть промпты
    const hasPrompts = prompts.length > 0;

    return {
        prompts,
        courses: hasPrompts ? allCourses : [],
        challenges: hasPrompts ? allChallenges : [],
        loading: isFetching,
        hasMore: cache?.hasMorePrompts ?? true,
        loadMore,
    };
};
